import type { ReportClassifier, Status } from '@/lib/types';

const scoreColor: Record<Status, string> = {
  ok: 'text-ok-deep',
  warn: 'text-warn-deep',
  crit: 'text-crit-deep',
};

const ringColor: Record<Status, string> = {
  ok: 'stroke-[var(--c-ok)]',
  warn: 'stroke-[var(--c-warn)]',
  crit: 'stroke-[var(--c-crit)]',
};

interface ClassifiersTabProps {
  classifiers: ReportClassifier[];
}

export function ClassifiersTab({ classifiers }: ClassifiersTabProps) {
  return (
    <div className="grid grid-cols-3 gap-2.5 max-[1024px]:grid-cols-2 max-[760px]:grid-cols-1">
      {classifiers.map((c) => (
        <article key={c.name} className="glass-panel p-5 px-[22px] flex flex-col gap-3">
          <header className="flex items-center justify-between gap-3">
            <h3 className="text-[14px] font-semibold tracking-[-0.005em] m-0 leading-[1.3]">
              {c.name}
            </h3>
            <ScoreRing score={c.score} status={c.status} />
          </header>
          <p className="text-[12.5px] text-tertiary leading-[1.5] m-0">{c.description}</p>
          {c.relatedCodes.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {c.relatedCodes.map((code) => (
                <span
                  key={code}
                  className="text-[10px] font-medium tracking-[0.02em] tnum px-1.5 py-0.5 rounded-sm bg-glass border border-line-glass text-secondary"
                >
                  {code}
                </span>
              ))}
            </div>
          )}
        </article>
      ))}
    </div>
  );
}

interface ScoreRingProps {
  score: number;
  status: Status;
}

function ScoreRing({ score, status }: ScoreRingProps) {
  const r = 18;
  const c = 2 * Math.PI * r;
  const dash = (score / 100) * c;
  return (
    <div className="relative w-[46px] h-[46px] shrink-0">
      <svg viewBox="0 0 44 44" className="w-full h-full -rotate-90">
        <circle cx="22" cy="22" r={r} fill="none" stroke="var(--border-default)" strokeWidth="3" />
        <circle
          cx="22"
          cy="22"
          r={r}
          fill="none"
          strokeWidth="3"
          strokeLinecap="round"
          className={ringColor[status]}
          strokeDasharray={`${dash} ${c - dash}`}
        />
      </svg>
      <div className={`absolute inset-0 grid place-items-center text-[14px] font-bold tnum tracking-[-0.02em] ${scoreColor[status]}`}>
        {score}
      </div>
    </div>
  );
}
